import { IconStepperProps } from '@legacy-vfuk/core-icon-stepper';
import { Tab, Tabs } from '@legacy-vfuk/core-tabs';
import axios from 'axios';
import { rem } from 'polished';
import { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';

import PricingCardCarousel from '@/components/pageBuilder/Sections/PricingCardCarousel';
import { TPricingCardCarousel } from '@/components/pageBuilder/Sections/PricingCardCarousel/PricingCardCarousel.types';
import isEnvDevelopment from '@/helpers/isEnvDevelopment';
import { useModalContext } from '@/hooks/modal/useModal';
import { TError } from '@/services/error.types';
import { retrieveProduct } from '@/services/product.services';

import { TabsWrapper, Wrapper } from './SWConfigureBundle.styles';
import { SWConfigureBundleProps } from './SWConfigureBundle.types';
import ConfigureSteps from './ConfigureSteps';
import SWSectionTariffCards from '../SWSectionTariffCards';
import { useAppDispatch, useAppSelector, setMaxSteps, setCurrentStep, resetStep } from '@/store';

const SWConfigureBundle: FC<SWConfigureBundleProps> = ({
  id,
  contentful_id,
  backgroundColor,
  title,
  bundleContent,
  __typename,
}) => {
  const dispatch = useAppDispatch();
  const { currentStep } = useAppSelector(({ userConfigureBundle }) => userConfigureBundle);
  const { setErrorInfo, toggleModal } = useModalContext();

  const [productsWithDxlInfo, setProductsWithDxlInfo] = useState<TPricingCardCarousel['products']>([]);
  const [itemStepName, setItemStepName] = useState<TPricingCardCarousel['itemStepName']>();
  const [steps, setSteps] = useState<IconStepperProps['steps']>([]);
  const [activeTab, setActiveTab] = useState<string>('');

  const { disableSteps, items, isUsingTariffCards } = bundleContent;
  const hasItems = items && items.length > 0;

  // Memoize content to prevent unnecessary re-renders and API call loops
  // This ensures the reference stays stable unless items actually change
  const content = useMemo(() => items.map(item => item.content), [items]);

  // Track if products have been fetched to prevent duplicate API calls
  const hasFetchedRef = useRef(false);

  // Generate a stable key based on product IDs to detect real changes
  const productIdsKey = useMemo(() => {
    if (!content) return '';
    return content
      .flat()
      .map(item => item?.productId)
      .filter(Boolean)
      .sort()
      .join(',');
  }, [content]);

  const handleProductError = useCallback(
    (error: unknown) => {
      const axiosError = axios.isAxiosError(error) ? error : undefined;
      setErrorInfo({
        ...(axiosError && {
          statusType: axiosError.response?.data?.error,
          statusCode: axiosError.response?.data?.code || axiosError.response?.status,
        }),
        requestUrl: 'catalogues',
      });
      toggleModal(true, 'Error');
    },
    [setErrorInfo, toggleModal]
  );

  const handleChangeTab = useCallback(
    (index: number) => {
      const { id, contentful_id, name } = items[index];
      setActiveTab(`${id}-${contentful_id}`);
      setItemStepName(name);
    },
    [items]
  );

  const handleChangeStep = useCallback(
    (index: number) => {
      if (hasItems) {
        dispatch(setCurrentStep(index + 1));
        handleChangeTab(index);
      }
    },
    [dispatch, hasItems, handleChangeTab]
  );

  const enrichProductWithDxlInfo = useCallback(
    async (productId: string) => {
      try {
        const response = await retrieveProduct(productId);

        if (axios.isAxiosError(response)) {
          throw response;
        }

        if (response.status !== 200 || !response.data?.id) {
          throw new Error('Invalid product response');
        }

        return response.data.dxlInformation;
      } catch (error) {
        handleProductError(error);
        return null;
      }
    },
    [handleProductError]
  );

  const processFreeProducts = useCallback(
    async (products: any[]) => {
      if (!products?.length) return;

      return Promise.all(
        products.map(async product => {
          const dxlInfo = await enrichProductWithDxlInfo(product.productId);
          if (dxlInfo) {
            product.dxlInformation = dxlInfo;
          }
          return product;
        })
      );
    },
    [enrichProductWithDxlInfo]
  );

  const processMainProducts = useCallback(async () => {
    if (!content) return;

    try {
      const products = await Promise.all(
        content.flatMap(itemGroup =>
          itemGroup.map(async item => {
            // Create a copy to avoid mutating the original item
            const itemCopy = { ...item };
            const dxlInfo = await enrichProductWithDxlInfo(item.productId);
            if (dxlInfo) {
              itemCopy.dxlInformation = dxlInfo;
            }

            if (item.productsFreeOfCharge) {
              await processFreeProducts(item.productsFreeOfCharge);
            }

            return itemCopy;
          })
        )
      );

      setProductsWithDxlInfo(products);
    } catch (error) {
      isEnvDevelopment() && console.error('Error processing products:', error);
    }
  }, [content, enrichProductWithDxlInfo, processFreeProducts]);

  // Fetch products only once when component mounts or when product IDs actually change
  // Using productIdsKey ensures we don't refetch just because of reference changes
  useEffect(() => {
    // Skip if no products or already fetched with same IDs
    if (!productIdsKey || hasFetchedRef.current) return;

    hasFetchedRef.current = true;
    dispatch(resetStep());
    processMainProducts();

    // Reset fetch flag when product IDs change
    return () => {
      hasFetchedRef.current = false;
    };
  }, [dispatch, processMainProducts, productIdsKey]);

  useEffect(() => {
    if (!hasItems) return;

    dispatch(setMaxSteps(items.length));
    handleChangeTab(0);
  }, [dispatch, hasItems, items, handleChangeTab]);

  useEffect(() => {
    if (!hasItems) return;

    const newSteps = items.map(({ name, iconName }, index) => ({
      onClick: () => handleChangeStep(index),
      title: iconName === 'bundles' ? 'Bundle' : name,
      icon: { name: iconName },
      ...(index + 1 < currentStep && { state: 'success' }),
    })) as IconStepperProps['steps'];

    setSteps(newSteps);
    handleChangeTab(currentStep - 1);
  }, [currentStep, hasItems, handleChangeStep, handleChangeTab, items]);

  if (!hasItems) return null;

  return (
    <Wrapper
      as="section"
      id={`${id}-${contentful_id}`}
      backgroundColor={backgroundColor}
      py={{ base: rem('30px'), md: 13 }}
    >
      <ConfigureSteps title={title} steps={steps} currentStep={currentStep} disableSteps={disableSteps} />

      <TabsWrapper
        as="section"
        pt={disableSteps ? { base: 8, md: rem('90px') } : { base: rem('40px'), md: rem('50px') }}
        step-number={currentStep}
        step-name={steps[currentStep]?.title || ''}
      >
        {productsWithDxlInfo && itemStepName && items && (
          <Tabs appearance="secondary" externallySetTabId={activeTab}>
            {items.map(item => (
              <Tab key={`${item.id}-${item.contentful_id}`} id={`${item.id}-${item.contentful_id}`}>
                {isUsingTariffCards ? (
                  <SWSectionTariffCards
                    __typename="ContentfulSectionSwTariffCards"
                    id={item.id}
                    name={item.name as string}
                    cards={item.tariffContent ?? []}
                    buttonsAction={item.buttonsAction}
                    isAddon={item.iconName === 'add-ons'}
                  />
                ) : (
                  <PricingCardCarousel
                    typename={__typename}
                    id={item.id}
                    contentful_id={item.contentful_id}
                    mainItem={items[0].content[0]}
                    name={item.name}
                    buttonsAction={item.buttonsAction}
                    buttonsAppearance={item.buttonsAppearance}
                    products={item.content}
                    itemStepName={itemStepName}
                  />
                )}
              </Tab>
            ))}
          </Tabs>
        )}
      </TabsWrapper>
    </Wrapper>
  );
};

export default SWConfigureBundle;
